import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/capacitor';
import WholeMeLoadingCircle from 'pages/wholeme/molecules/wholeMeLoadingCircle/WholeMeLoadingCircle';
import ProfileIntakeStepPage from 'pages/wholeme/molecules/profileIntakeStepPage/ProfileIntakeStepPage';
import { usePhotoGallery } from 'hooks/usePhotoGallery';
import WholeMeProfilePurposeVideoThumbnail from 'pages/wholeme/molecules/wholeMeProfilePurposeVideoThumbnail/wholeMeProfilePurposeVideoThumbnail';
import { Stack, Typography } from '@mui/material';
import useFetch, { CachePolicies } from 'use-http';
import { useHistory } from 'react-router-dom';
import { useAppState } from '../../../../../providers/app-state/app-state-provider';
import { firstOf } from '../../../util/profile_util';
import appStrings from '../../../../../common/app_strings';
import appColors from '../../../../../common/app_colors';
import MainImage from '../../../molecules/mainImage/MainImage';
import BaseModal from '../../../../../common/atoms/BaseModal/BaseModal';
import VideoPlayer from '../../../../../components/VideoPlayer';

const {
  wholeMeProfilePurposeVideo: {
    intakeTitle,
    intakeSubtitle,
    seeExample,
    skip,
  },
} = appStrings;
const { text } = appColors;

const ProfileIdentityIntakePurposeVideoPage = ({
  paginationMeta,
  nextOnClick,
  prevOnClick,
  wholeMeClient,
  exitIconOnClickOverride,
  isFromGrow,
}) => {
  const [previewMeta, setPreviewMeta] = useState(null);
  const [video, setVideo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [example, setExample] = useState(null);
  const [showExample, setShowExample] = useState(false);
  const history = useHistory();

  const handleNext = () => {
    if (isFromGrow) {
      history.push('/tabs/feed');
    } else {
      nextOnClick();
    }
  };

  const {
    get, response,
  } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  });

  useEffect(() => {
    setIsLoading(true);
    try {
      const fetchExampleData = async () => {
        const data = await get('/workflow-meta/sample-intro-video');
        if (response.ok && showExample) {
          setExample(data?.media[0]);
        }
      };
      if (showExample) {
        fetchExampleData();
      }
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      setIsLoading(false);
    }
  }, [showExample]);

  const {
    platform: { isNativePlatform },
  } = useAppState();
  const { takeVideo, takeWebVideo } = usePhotoGallery();

  useEffect(() => {
    wholeMeClient.fetchPurposeVideoState().then((state) => {
      setPreviewMeta({
        uri: firstOf(state?.purpose_video)?.uri,
      });
    });
  }, []);

  const handleWebVideoUpload = async () => {
    const { media } = await takeWebVideo();
    if (media) {
      setVideo({ id: media.id });
      setPreviewMeta({ uri: media.uri });
    }
  };

  const handleMobileVideoUpload = async () => {
    const { media, webviewPath } = await takeVideo();
    if (media) {
      setVideo({ webviewPath, id: media.id });
      setPreviewMeta({ uri: webviewPath });
    }
  };

  const handleVideoUpload = async () => {
    if (isLoading) {
      return;
    }
    const uploader = isNativePlatform ? handleMobileVideoUpload : handleWebVideoUpload;
    try {
      setIsLoading(true);
      await uploader();
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveVideo = async () => {
    if (isLoading) {
      return;
    }
    if (!video?.id) {
      handleNext();
      return;
    }
    await wholeMeClient.savePurposeVideoState(video.id);
    handleNext();
  };

  const handleModalClose = () => {
    setShowExample(false);
  };

  const handleModalOpen = () => {
    setShowExample(true);
  };

  return (
    <>
      <ProfileIntakeStepPage
        paginationMeta={paginationMeta}
        title={intakeTitle}
        subtitle={intakeSubtitle}
        prevButton={{ onClick: prevOnClick }}
        nextButton={{ onClick: handleSaveVideo }}
        backgroundOverlay="/assets/profile/identity/profile-picture-overlay.svg"
        exitIconOnClickOverride={exitIconOnClickOverride}
        linkedTextUnderNavControl={skip}
        linkedTextStyleOverride={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginTop: '-10px',
          fontWeight: 600,
          fontSize: '12px',
        }}
      >
        {(!previewMeta || isLoading) ? (
          <WholeMeLoadingCircle />
        ) : (
          <Stack alignItems="center">
            <WholeMeProfilePurposeVideoThumbnail videoUrl={previewMeta?.uri} onClick={handleVideoUpload} />
            <Typography sx={{ color: text.navy, fontWeight: 700 }} onClick={handleModalOpen}>
              {seeExample}
            </Typography>
          </Stack>
        )}
        <MainImage
          imageSrc="/assets/profile/wholeMe/video/video-hero-img.svg"
          imageAlt="video upload"
          containerStyleOverride={{ marginTop: '-50px', marginBottom: '-40px' }}
          imageStylesOverride={{ height: '200px', width: null }}
        />
      </ProfileIntakeStepPage>
      <BaseModal
        isOpen={showExample}
        handleClose={handleModalClose}
        showExit
        modalOverrideStyles={{ minWidth: '300px', minHeight: 'fit-content' }}
      >
        <Stack spacing={2} sx={{ p: '5px' }}>
          <VideoPlayer
            videoUrl={example?.video_url}
          />
        </Stack>
      </BaseModal>
    </>
  );
};
export default ProfileIdentityIntakePurposeVideoPage;
